import { Link, useSearch } from "@tanstack/react-router";
import { CircleAlert, CircleCheck, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { verifyEmail } from "@/lib/auth/auth-api";

import { AuthScreen } from "./auth-screen";

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const { key = "" } = useSearch({ strict: false }) as { key?: string };
  const [status, setStatus] = useState<"verifying" | "done" | "error">(
    key ? "verifying" : "error",
  );
  // Guard against the double-invocation of effects in React StrictMode so the
  // one-shot verification POST fires exactly once.
  const started = useRef(false);

  useEffect(() => {
    if (!key || started.current) return;
    started.current = true;
    verifyEmail(key)
      .then(() => setStatus("done"))
      .catch(() => setStatus("error"));
  }, [key]);

  if (status === "verifying") {
    return (
      <AuthScreen>
        <div className="text-center">
          <Loader2 className="mx-auto size-8 animate-spin text-primary" aria-hidden />
          <h1 className="mt-3.5 text-2xl font-extrabold">
            {t("auth.verify.verifyingTitle")}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("auth.verify.verifyingBody")}
          </p>
        </div>
      </AuthScreen>
    );
  }

  if (status === "done") {
    return (
      <AuthScreen>
        <div className="text-center">
          <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-success/10">
            <CircleCheck className="size-6 text-success" aria-hidden />
          </div>
          <h1 className="text-2xl font-extrabold">{t("auth.verify.successTitle")}</h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("auth.verify.successBody")}
          </p>
          <Button asChild className="mt-5 w-full">
            <Link to="/login">{t("auth.verify.goToLogin")}</Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="text-center">
        <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-destructive/10">
          <CircleAlert className="size-6 text-destructive" aria-hidden />
        </div>
        <h1 className="text-2xl font-extrabold">{t("auth.verify.errorTitle")}</h1>
        <p className="mt-1.5 text-[14.5px] text-muted-foreground">
          {t("auth.verify.errorBody")}
        </p>
        <Button asChild className="mt-5 w-full">
          <Link to="/login">{t("auth.verify.goToLogin")}</Link>
        </Button>
      </div>
    </AuthScreen>
  );
}
